import { gql, NetworkStatus, useLazyQuery, useQuery } from '@apollo/client'
import { useEffect } from 'react'

import {
  CollectionItemDetailQuery,
  CollectionItemDetailQueryVariables,
  HasItemUserItemDetail,
  HasItemUserItemDetailQuery,
  HasItemUserItemDetailQueryVariables,
  ItemImageAreaFragmentDoc,
  ItemSerial,
  Maybe,
  SerialItemOwnersFragmentDoc,
  TradableUsersConnectionFragmentDoc,
  TradableUsersQuery,
  TradableUsersQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useInitialLoading } from '@/components/lib/initialLoading'
import { config } from '@/config'
import { useCurrentUser, useLoginModal, useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'
import { decodeSkuId } from '@/core/sku'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, generatePath, routerPages } =
    useRouter<{ itemId: string; skuId?: string }>()
  const { user } = useCurrentUser()
  const { analytics } = useAnalytics()
  const { showModal } = useLoginModal()

  const { data, loading } = useQuery<
    CollectionItemDetailQuery,
    CollectionItemDetailQueryVariables
  >(QUERY, {
    variables: {
      itemId: router.query.itemId,
    },
    returnPartialData: true,
  })

  const hasSkuItem = (
    serials: Array<{ serial?: Maybe<Pick<ItemSerial, 'skuId'>> }>
  ) => {
    // NOTE: serialsがないかskuIdがない場合はチェックをパス
    if (!serials || !router.query.skuId) return true
    const list = serials.find(
      s => s.serial?.skuId === decodeSkuId(router.query.skuId!)
    )
    return list ? true : false
  }

  const { loading: loadingItemUserItemDetail } = useQuery<
    HasItemUserItemDetailQuery,
    HasItemUserItemDetailQueryVariables
  >(HasItemUserItemDetail, {
    variables: { itemId: router.query.itemId, userId: user?.uid },
    skip: !user?.isSigned,
    onCompleted({ portfolio }) {
      //If the user owns the item, navigate to the OwnedItemDetail screen
      if (portfolio?.id && hasSkuItem(portfolio?.serials)) {
        router.replace(
          `${generatePath(routerPages.OwnedItemDetail, {
            itemId: router.query.itemId,
          })}${router.query.skuId ? `?skuId=${router.query.skuId}` : ''}`
        )
      }
    },
  })

  const [
    getTradableUsers,
    {
      data: dataTradableUsers,
      fetchMore,
      networkStatus,
      loading: loadingTradableUsers,
    },
  ] = useLazyQuery<TradableUsersQuery, TradableUsersQueryVariables>(
    QUERY_TRADABLE_USERS
  )
  const unit = data?.item?.skuSettings?.units.find(unit => {
    return unit.id === decodeSkuId(router.query.skuId ?? '')
  })
  const item = data?.item ? { ...data.item, sku: unit ?? data.item.sku } : null

  useEffect(() => {
    if (!user) {
      showModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  return {
    state: {
      loading: useInitialLoading(data, loading || loadingItemUserItemDetail),
      isLoggedIn: !!user,
      loadingTradableUsers,
      fetching: networkStatus === NetworkStatus.fetchMore,
      hasNextPage:
        !!dataTradableUsers?.item?.tradableUsers?.pageInfo?.hasNextPage,
      item: item,
      tradableUsers: dataTradableUsers?.item?.tradableUsers ?? null,
    },
    actions: {
      async onReply() {},
      async onGetTradableUsers() {
        const { data } = await getTradableUsers({
          variables: {
            itemId: router.query.itemId,
            pageInput: {
              /**
               * FIXME: トレード可能ユーザー一覧の表示にて、item.tradableUsersから
               * 「手持ちの(itemIdの)アイテムをすべてプロテクトしている」ユーザーを非表示にする処理を行なっており、ページネーションの動作が悪い。
               * そのため暫定的にlimitを上げることで解消している
               * https://app.clickup.com/t/908127/UT-15015
               */
              limit: 100,
            },
          },
        })
        if (!data?.item?.tradableUsers?.edges?.length) {
          analytics?.track('impression_noData', {
            applicationKey: config.applicationKey,
            itemId: data?.item?.id ?? '#',
            name: 'tradableUsers',
          })
        }
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            itemId: router.query.itemId,
            pageInput: {
              cursor:
                dataTradableUsers?.item?.tradableUsers?.pageInfo.endCursor,
              limit: 100,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query CollectionItemDetail($itemId: ID!) {
    item(id: $itemId) {
      id
      issuedArtist {
        id
      }
      price
      formattedPrice @client
      paymentType
      serials(pageInput: { limit: 1 }) {
        ...SerialItemOwners
      }
      collection {
        id
        isPublishOnStore
      }
      ...ItemImageArea
      description
      commentCount
      isPublished
      gachas {
        id
        name
        thumbnail {
          uri
          mediumUri
        }
      }
    }
    user {
      id
      displayName
      avatar {
        uri
        smallUri
      }
    }
  }
  ${ItemImageAreaFragmentDoc}
  ${SerialItemOwnersFragmentDoc}
`

const QUERY_TRADABLE_USERS = gql`
  query TradableUsers($itemId: ID!, $pageInput: PageInput) {
    item(id: $itemId) {
      id
      tradableUsers(pageInput: $pageInput) {
        ...TradableUsersConnection
      }
    }
  }
  ${TradableUsersConnectionFragmentDoc}
`
